import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';

import { Subject, debounceTime, distinctUntilChanged, fromEvent, map, merge, takeUntil } from 'rxjs';

import { InputExpandableFieldComponent, InputFieldComponent, InputType } from '../form';
import { IconsName, SizeOptionType } from '../svg-icon';

@Component({
  selector: 'shrd-uic-search-field',
  templateUrl: './search-field.component.html',
  styleUrls: ['./search-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class SearchFieldComponent implements AfterViewInit, OnDestroy {
  @Input() type: InputType = 'search';
  @Input() name: string;
  @Input() inputId: string;
  @Input() placeholder = '';
  @Input() screenReaderLabel = '';
  @Input() autofocus = false;
  @Input() screenReaderLabelDesktopOnly = false;
  @Input() disabled = false;
  @Input() headerVariant = false;
  @Input() isBarcodeScannerEnabled = false;
  @Input() barcodeScannerIcon: IconsName.SCAN | IconsName.CAMERA = IconsName.SCAN;
  @Input() value = null;
  @Input() barcodeTitle = 'Scan barcode';
  @Input() buttonTitle = 'Click to search';
  @Input() resetTitle = 'Reset';
  @Input() iconSize: SizeOptionType = 'small';
  @Input() restoreInitialStateOnEmptySearch = false;
  @Input() usePreloader = false;
  @Input() loading = false;
  @Input() maxlength = 100;

  @HostBinding('class.input-group-expandable')
  @Input()
  expandOnFocusSize: string;
  @Input() maxWidth?: string;

  @Output() valueChange = new EventEmitter<string>();
  @Output() searchTextChange = new EventEmitter<string>();
  @Output() searchEventTriggered = new EventEmitter<MouseEvent | KeyboardEvent>();
  @Output() searchReset = new EventEmitter<void>();
  @Output() focusChange = new EventEmitter<boolean>();
  @Output() barcodeScannerTriggered = new EventEmitter<void>();

  @HostBinding('class.shrd-uic-search-field') readonly hostClass = true;
  @HostBinding('class.header-variant')
  get headerVariantClass(): boolean {
    return this.headerVariant;
  }

  @ViewChild('searchField') searchField: InputFieldComponent;
  @ViewChild(InputExpandableFieldComponent) inputExpandableFieldComponent: InputExpandableFieldComponent;

  get searchControl(): UntypedFormControl {
    return this.searchField.control;
  }

  unsubscribe$ = new Subject<void>();
  iconName = IconsName;

  constructor(private readonly cdr: ChangeDetectorRef, private readonly elRef: ElementRef) {}

  ngAfterViewInit(): void {
    this.subscribeToFocusChange();
    this.subscribeValChange();
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  onSearch(searchEvent?: MouseEvent | KeyboardEvent, element?: HTMLElement): void {
    this.emitValue();
    // hide virtual keyboard on mobile devices
    this.searchField.inputElementRef.nativeElement.blur();

    // in case an event was passed
    if (searchEvent) {
      // emit the search event triggered output
      this.searchEventTriggered.emit(searchEvent);
    }

    // reset to default state if search empty
    if (this.restoreInitialStateOnEmptySearch && !this.searchControl.value) {
      element.blur();
    }
  }

  resetValue(): void {
    this.focusInput();
    this.searchControl.setValue(null);
    this.emitValue();
    this.searchReset.emit();
  }

  public reset(): void {
    this.searchControl.setValue(null);
    this.cdr.markForCheck();
  }

  public focus(): void {
    this.searchField.focus();
  }

  public blur(): void {
    this.searchField.blur();
  }

  public restoreInitialState(ele: HTMLElement): void {
    ele.blur();
    this.inputExpandableFieldComponent?.restoreInputInitialState();
  }

  private subscribeValChange(): void {
    this.searchControl.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(val => this.searchTextChange.emit(val));
  }

  /**
   * listen to focus changes, but debounce the events so a focus change won't be emitted
   * just by changing the focus from input to search or reset button.
   * @private
   */
  private subscribeToFocusChange(): void {
    const hostEl: HTMLElement = this.elRef.nativeElement;
    merge(...['focusin', 'focusout'].map(e => fromEvent<FocusEvent>(hostEl, e)))
      .pipe(
        debounceTime(10),
        distinctUntilChanged((a, b) => a.type === b.type),
        map(e => e.type === 'focusin'),
        takeUntil(this.unsubscribe$)
      )
      .subscribe(focused => {
        this.focusChange.emit(focused);
      });
  }

  private emitValue(): void {
    this.valueChange.emit(this.searchControl.value);
  }

  private focusInput(): void {
    this.searchField.inputElementRef.nativeElement.focus();
  }
}
